import { useEffect } from "react";
import { TextPageLayout } from "@/components/TextPageLayout";
import { setMetaDescription, setCanonicalUrl, setOpenGraphTags, setTwitterCardTags } from "@/lib/seo";
import { generateWebPageSchema, addSchemaToPage } from "@/lib/schema";

export default function SecurityPage() {
  useEffect(() => {
    const title = "Security - Munthly";
    const description = "Learn about Munthly's security measures and how we protect your financial data. Understand our commitment to keeping your information safe.";
    
    document.title = title;
    setMetaDescription(description);
    const canonicalCleanup = setCanonicalUrl("/security");
    const ogCleanup = setOpenGraphTags(title, description, "/security");
    const twitterCleanup = setTwitterCardTags(title, description);
    
    // Add schema for security page
    const webPageSchema = generateWebPageSchema(
      "/security",
      title,
      description
    );
    
    const schemaCleanup = addSchemaToPage(webPageSchema);
    
    return () => {
      canonicalCleanup();
      ogCleanup();
      twitterCleanup();
      schemaCleanup();
    };
  }, []);

  return (
    <TextPageLayout
      title="Security"
      subtitle="How we protect your data"
    >
      <div className="space-y-6 text-base leading-relaxed">
        <p>
          At Munthly, we take the security and privacy of your financial data seriously. Your information is protected using industry-standard security practices.
        </p>

        <h2 className="text-xl font-semibold mt-8 mb-4">Password Security</h2>
        <p>
          Your password is never stored in plain text. We use industry-standard password hashing to protect your credentials. This means your password cannot be recovered by anyone, including our team. If you forget your password, you'll need to reset it.
        </p>
        <p>
          We require passwords to be at least 8 characters long to help ensure account security.
        </p>

        <h2 className="text-xl font-semibold mt-8 mb-4">Secure Authentication</h2>
        <p>
          We use secure authentication tokens to verify your identity when you log in. These tokens expire automatically to help keep your account secure. You'll need to log in again after your session expires.
        </p>

        <h2 className="text-xl font-semibold mt-8 mb-4">Data Access Control</h2>
        <p>
          Your financial data is stored securely and is only accessible through your authenticated account. All requests to access your data require valid authentication. This means only you can view and manage your financial information.
        </p>
        <p>
          Each user's data is kept separate, and there are strict controls in place to prevent unauthorized access.
        </p>

        <h2 className="text-xl font-semibold mt-8 mb-4">Data Privacy</h2>
        <p>
          We do not share your financial data with third parties. Your information is used solely to provide you with the Munthly service. We do not sell, rent, or trade your personal or financial information.
        </p>
        <p>
          Your data belongs to you, and we're committed to keeping it private and secure.
        </p>

        <h2 className="text-xl font-semibold mt-8 mb-4">Secure Connections</h2>
        <p>
          All data transmitted between your device and our servers uses secure, encrypted connections. This ensures that your information cannot be intercepted or read by third parties during transmission.
        </p>

        <h2 className="text-xl font-semibold mt-8 mb-4">Your Role in Security</h2>
        <p>
          While we take every precaution to secure your data, you also play an important role in keeping your account safe:
        </p>
        <ul className="list-disc list-inside space-y-2 ml-4 mt-2 text-muted-foreground">
          <li>Use a strong, unique password that you don't use elsewhere</li>
          <li>Never share your login credentials with anyone</li>
          <li>Log out when using shared or public devices</li>
          <li>Keep your device and browser software updated</li>
          <li>Be cautious of phishing attempts or suspicious emails</li>
        </ul>

        <h2 className="text-xl font-semibold mt-8 mb-4">Data Backup</h2>
        <p>
          We recommend that you keep your own records of important financial information. While we take measures to protect your data, it's always good practice to maintain your own backups of critical financial data.
        </p>

        <h2 className="text-xl font-semibold mt-8 mb-4">Reporting Security Concerns</h2>
        <p>
          If you discover a security vulnerability or have concerns about the security of your account, please contact us immediately. We take all security reports seriously and will investigate promptly.
        </p>

        <div className="bg-[var(--surface-container)] rounded-2xl p-6 mt-8">
          <h3 className="font-semibold mb-2">Security is an ongoing commitment</h3>
          <p className="text-sm text-muted-foreground">
            We continuously review and update our security practices to protect your data. Security measures may be updated over time to address new threats and maintain the highest standards of protection.
          </p>
        </div>
      </div>
    </TextPageLayout>
  );
}
